import { useCallback, useMemo } from 'react'

import { matchPath } from 'react-router-dom'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { GALLERY_IMAGE_PATH_PATTERN, STUDIO_PATH, buildGalleryImagePath } from 'Pods/ThreeDStudio/constants'

import { NOOP } from 'Utils/stableDefaults'

import useGalleryImageBySlug from './useGalleryImageBySlug'

// Which image the modal shows, read off /ai-image-generator/images/:slug so the URL can be
// shared, plus previous/next over the loaded list. Every move pushes a history entry, so the
// browser's Back steps back through the images viewed before it leaves the modal.
//
// The list only ever grows forward from page 1, so there is nothing to fetch behind the
// first image; going back simply stops there. Forward is different: on the last loaded
// image the arrow stays live while the query reports another page, pulls that page in and
// steps into the first image of it. `loadMore` resolves with the query result, so the
// appended images are read straight off it rather than waiting on a re-render.
//
// A slug the loaded pages do not hold (a shared link, or an image past them) is fetched on
// its own and shown without arrows, as there is no position in the list to step from.
function useImageModal(images, { hasNextPage = false, isFetchingNextPage = false, loadMore = NOOP } = {}) {
  const location = useLocation()
  const navigate = useNavigate()
  const openedSlug = matchPath(GALLERY_IMAGE_PATH_PATTERN, location?.pathname ?? '')?.params?.slug ?? null

  const openedIndex = useMemo(
    () => (openedSlug ? images.findIndex(image => image.slug === openedSlug) : -1),
    [images, openedSlug],
  )
  const isListed = openedIndex !== -1
  const fetchedImage = useGalleryImageBySlug(isListed ? null : openedSlug)
  const openedImage = isListed ? images[openedIndex] : fetchedImage
  const hasPrevious = openedIndex > 0
  const hasLoadedNext = isListed && openedIndex < images.length - 1
  const hasNext = hasLoadedNext || (isListed && hasNextPage)

  const open = useCallback(({ slug }) => {
    if (slug) navigate(buildGalleryImagePath(slug))
  }, [navigate])

  const close = useCallback(() => navigate(STUDIO_PATH.COMMUNITY_IMAGES), [navigate])

  const previous = useCallback(() => {
    if (hasPrevious) open(images[openedIndex - 1])
  }, [hasPrevious, images, openedIndex, open])

  const next = useCallback(async () => {
    if (hasLoadedNext) {
      open(images[openedIndex + 1])
      return
    }
    if (!isListed || !hasNextPage || isFetchingNextPage) return

    const result = await loadMore()
    const appended = result?.data?.pages?.flatMap(page => page.items) ?? []
    const following = appended[openedIndex + 1]

    if (following) open(following)
  }, [hasLoadedNext, images, openedIndex, isListed, hasNextPage, isFetchingNextPage, loadMore, open])

  return {
    openedImage, hasPrevious, hasNext, open, close, previous, next,
  }
}

export default useImageModal
